import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const Courses = () => {
    const { t, i18n } = useTranslation();
    const [courses, setCourses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [formData, setFormData] = useState({
        courseCode: '',
        courseNameAr: '',
        courseNameEn: '',
        departmentId: '',
        teacherId: '',
        targetGender: 'both'
    });
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [coursesRes, deptRes, teachersRes] = await Promise.all([
                api.get('/admin/courses'),
                api.get('/admin/departments'),
                api.get('/admin/teachers')
            ]);

            if (coursesRes.data.success) setCourses(coursesRes.data.data);
            if (deptRes.data.success) setDepartments(deptRes.data.data);
            if (teachersRes.data.success) setTeachers(teachersRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (course = null) => {
        if (course) {
            setIsEditing(true);
            setSelectedCourseId(course.course_id);
            setFormData({
                courseCode: course.course_code,
                courseNameAr: course.course_name_ar,
                courseNameEn: course.course_name_en,
                departmentId: course.department_id,
                targetGender: course.target_gender || 'both',
                teacherId: course.teacher_id || ''
            });
        } else {
            setIsEditing(false);
            setSelectedCourseId(null);
            setFormData({
                courseCode: '',
                courseNameAr: '',
                courseNameEn: '',
                departmentId: '',
                teacherId: '',
                targetGender: 'both'
            });
        }
        setFormError('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        try {
            let response;
            if (isEditing) {
                response = await api.put(`/admin/courses/${selectedCourseId}`, formData);
            } else {
                response = await api.post('/admin/courses', formData);
            }

            if (response.data.success) {
                fetchData();
                setShowModal(false);
            }
        } catch (error) {
            setFormError(error.response?.data?.message || t('common.error'));
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('admin.courses.deleteConfirm'))) return;

        try {
            const response = await api.delete(`/admin/courses/${id}`);
            if (response.data.success) {
                fetchData();
            }
        } catch (error) {
            alert(error.response?.data?.message || t('common.error'));
        }
    };

    const isAr = i18n.language === 'ar';

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="courses-page fade-in">
            <div className="page-header">
                <h1>{t('admin.courses.title')}</h1>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    <span className="material-icons">add</span> {t('admin.courses.addCourse')}
                </button>
            </div>

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t('admin.courses.courseCode')}</th>
                            <th>{t('admin.courses.courseName')}</th>
                            <th>{t('admin.courses.department')}</th>
                            <th>{t('admin.courses.assignedTeacher')}</th>
                            <th>{t('admin.courses.studentsCount')}</th>
                            <th>Target Gender</th>
                            <th>{t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {courses.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                                    {t('common.noData')}
                                </td>
                            </tr>
                        ) : (
                            courses.map((course, index) => (
                                <tr key={course.course_id}>
                                    <td>{index + 1}</td>
                                    <td><strong>{course.course_code}</strong></td>
                                    <td>{isAr ? course.course_name_ar : course.course_name_en}</td>
                                    <td>
                                        <span className="badge badge-primary">
                                            {isAr ? course.department_name_ar : course.department_name_en}
                                        </span>
                                    </td>
                                    <td>{isAr ? course.teacher_name_ar : course.teacher_name_en || '-'}</td>
                                    <td><strong>{course.students_count}</strong></td>
                                    <td>{course.target_gender || 'both'}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn btn-edit btn-sm"
                                                onClick={() => handleOpenModal(course)}
                                            >
                                                <span className="material-icons">edit</span>
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleDelete(course.course_id)}
                                            >
                                                <span className="material-icons">delete</span>
                                                {t('common.delete')}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? t('common.edit') : t('admin.courses.addCourse')}</h2>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {formError && (
                                <div className="alert alert-danger">
                                    <span className="material-icons">error_outline</span> {formError}
                                </div>
                            )}

                            <div className="form-group">
                                <label className="label">{t('admin.courses.courseCode')}</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={formData.courseCode}
                                    onChange={(e) => setFormData({ ...formData, courseCode: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="label">{t('admin.courses.courseNameAr')}</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={formData.courseNameAr}
                                    onChange={(e) => setFormData({ ...formData, courseNameAr: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="label">{t('admin.courses.courseNameEn')}</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={formData.courseNameEn}
                                    onChange={(e) => setFormData({ ...formData, courseNameEn: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="label">{t('admin.courses.department')}</label>
                                <select
                                    className="select"
                                    value={formData.departmentId}
                                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                                    required
                                >
                                    <option value="">{t('admin.courses.selectDepartment')}</option>
                                    {departments.map(dept => (
                                        <option key={dept.department_id} value={dept.department_id}>
                                            {isAr ? dept.department_name_ar : dept.department_name_en}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="label">{t('admin.courses.assignedTeacher')}</label>
                                <select
                                    className="select"
                                    value={formData.teacherId}
                                    onChange={(e) => setFormData({ ...formData, teacherId: e.target.value })}
                                >
                                    <option value="">{t('admin.courses.selectTeacher')}</option>
                                    {teachers.map(teacher => (
                                        <option key={teacher.user_id} value={teacher.user_id}>
                                            {isAr ? teacher.full_name_ar : teacher.full_name_en}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="label">Target Gender</label>
                                <select
                                    className="select"
                                    value={formData.targetGender}
                                    onChange={(e) => setFormData({ ...formData, targetGender: e.target.value })}
                                >
                                    <option value="both">Both</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </select>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                                    {formLoading ? t('common.loading') : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Courses;
